import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowBack,
  Close,
  Delete,
  DoneAll,
  Settings
} from '@mui/icons-material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { Placeholder } from '@/pages/CRM/components/Placeholder';

import { useNotificationFeed } from '../hooks/useNotificationFeed';
import { useOpenNotificationPreferences } from '../hooks/useOpenNotificationPreferences';

import NotificationCard from './NotificationCard';
import type { Notification } from './notificationFromSuprSend';

const SCROLL_FETCH_THRESHOLD_PX = 48;

type NotificationsPanelLayout = 'popover' | 'fullScreen';

const Header = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
  minHeight: '62px',
  padding: '0 16px',
  borderBottom: '1px solid var(--palette-semantic-divider-primary)',
  backgroundColor: 'var(--palette-semantic-background-base)'
});

const NotificationList = styled(Box)({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto'
});

const Footer = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  minHeight: '52px',
  padding: '8px',
  borderTop: '1px solid var(--palette-semantic-divider-primary)',
  backgroundColor: 'var(--palette-semantic-background-base)'
});

const FooterButton = styled(Button)({
  color: 'var(--palette-semantic-text-secondary)',
  fontSize: '14px',
  fontWeight: 400,
  padding: '6px 8px',
  '& .MuiButton-startIcon': {
    marginLeft: 0,
    marginRight: '8px',
    color: 'var(--palette-semantic-icon-quaternary)',
    '& > .MuiSvgIcon-root': { fontSize: '16px' }
  }
});

interface NotificationsPanelProps {
  layout: NotificationsPanelLayout;
  onClose: () => void;
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  layout,
  onClose
}) => {
  const {
    notifications,
    canMarkAllAsRead,
    canArchiveAll,
    isLoading,
    isFetchingMore,
    isEmpty,
    isArchivingAll,
    hasUnloadedUnread,
    fetchNextPage,
    markAllAsRead,
    markAsInteracted,
    archiveNotification,
    archiveAll
  } = useNotificationFeed();
  const [isCatchingUpUnread, setIsCatchingUpUnread] = useState(true);
  const isShowingUnreadCatchUp = isCatchingUpUnread && hasUnloadedUnread;
  const listRef = useRef<HTMLDivElement>(null);
  const wasReadOnOpenRef = useRef(new Map<string, boolean>());
  const navigate = useNavigate();

  for (const notification of notifications) {
    if (!wasReadOnOpenRef.current.has(notification.n_id)) {
      wasReadOnOpenRef.current.set(
        notification.n_id,
        Boolean(notification.read_on)
      );
    }
  }
  const wasReadOnOpen = (notification: Notification) =>
    wasReadOnOpenRef.current.get(notification.n_id) ?? false;
  const sortedNotifications = [...notifications].sort(
    (a, b) =>
      Number(wasReadOnOpen(a)) - Number(wasReadOnOpen(b)) ||
      b.created_on - a.created_on
  );

  const navigateFromNotification = (path: string) => {
    onClose();
    navigate(path);
  };

  useEffect(() => {
    if (!isCatchingUpUnread || isLoading) {
      return;
    }
    if (hasUnloadedUnread) {
      fetchNextPage();
    } else {
      setIsCatchingUpUnread(false);
    }
  }, [isCatchingUpUnread, isLoading, hasUnloadedUnread, fetchNextPage]);

  useEffect(() => {
    const list = listRef.current;
    if (list && list.scrollHeight <= list.clientHeight) {
      fetchNextPage();
    }
  });
  const openPreferences = useOpenNotificationPreferences(onClose);
  const isFullScreen = layout === 'fullScreen';

  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_FETCH_THRESHOLD_PX) {
      fetchNextPage();
    }
  };

  return (
    <Stack height="100%" minHeight={0}>
      <Header>
        {isFullScreen && (
          <IconButton
            edge="start"
            aria-label="Close notifications"
            onClick={onClose}
          >
            <ArrowBack htmlColor="var(--palette-semantic-icon-secondary)" />
          </IconButton>
        )}
        <Typography
          component="h2"
          color="var(--palette-semantic-text-primary)"
          fontWeight={500}
          fontSize="18px"
        >
          Notifications
        </Typography>
        {!isFullScreen && (
          <IconButton
            aria-label="Close notifications"
            onClick={onClose}
            sx={{ width: 32, height: 32, ml: 'auto', mr: '-6px' }}
          >
            <Close
              htmlColor="var(--palette-semantic-icon-tertiary)"
              sx={{ width: 20, height: 20 }}
            />
          </IconButton>
        )}
      </Header>

      <NotificationList ref={listRef} onScroll={handleScroll}>
        {isLoading || isShowingUnreadCatchUp ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={24} />
          </Stack>
        ) : isEmpty ? (
          <Placeholder
            Icon={NotificationsIcon}
            sx={{ height: '100%', p: 2 }}
            avatarSxProps={{
              width: 76,
              height: 76,
              background: 'var(--palette-semantic-background-tertiary)'
            }}
            iconSxProps={{
              width: 40,
              height: 40,
              color: 'var(--palette-semantic-icon-accent)'
            }}
            headerStyle={{
              fontSize: '14px',
              lineHeight: '20px',
              color: 'var(--palette-semantic-text-primary)'
            }}
            header="There are no unread notifications"
          />
        ) : (
          sortedNotifications.map((notification) => (
            <NotificationCard
              key={notification.n_id}
              notification={notification}
              isDeleteAlwaysVisible={isFullScreen}
              onArchive={() => archiveNotification(notification)}
              onInteract={() => markAsInteracted(notification)}
              onNavigate={navigateFromNotification}
            />
          ))
        )}
        {isFetchingMore && !isShowingUnreadCatchUp && (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={20} />
          </Stack>
        )}
      </NotificationList>

      <Footer>
        {!isEmpty && (
          <>
            <FooterButton
              variant="text"
              size="medium"
              startIcon={<DoneAll />}
              disabled={!canMarkAllAsRead}
              onClick={markAllAsRead}
            >
              Mark all as read
            </FooterButton>
            <FooterButton
              variant="text"
              size="medium"
              startIcon={<Delete />}
              disabled={!canArchiveAll}
              loading={isArchivingAll}
              loadingPosition="start"
              onClick={archiveAll}
            >
              Delete all
            </FooterButton>
          </>
        )}
        <FooterButton
          variant="text"
          size="medium"
          startIcon={<Settings />}
          onClick={openPreferences}
        >
          {isFullScreen ? 'Preferences' : 'Notification Preferences'}
        </FooterButton>
      </Footer>
    </Stack>
  );
};

export default NotificationsPanel;
